import { useCallback, useEffect, useMemo, useState } from 'react'

import { OLMSApiService } from '@/shared/apis'
import { GridColDef } from '@mui/x-data-grid-premium'
import { EntityId } from '@reduxjs/toolkit'

import { RunsheetSummary, ScenarioSummary } from '../models/ScheduleSummary'

export const useSummaryTableData = (id: EntityId, layoutType: 'planning' | 'runsheet', showSummaryTable: boolean) => {
  const [kpis, setKpis] = useState<ScenarioSummary | RunsheetSummary | null>(null)
  const [loading, setLoading] = useState(false)

  const fetchSummary = useCallback(async () => {
    if (!layoutType) return
    setLoading(true)

    if (layoutType === 'runsheet') {
      const data = await OLMSApiService.get<RunsheetSummary>(`runsheet/kpiSummary/${id}`)

      setKpis(data)
    }
    else {
      const data = await OLMSApiService.get<ScenarioSummary>(`/planning/kpiSummary/${id}`)

      setKpis(data)
    }

    setLoading(false)
  }, [layoutType, id])

  useEffect(() => {
    if (!id || !showSummaryTable || kpis) return

    fetchSummary()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutType, id, showSummaryTable])

  const summaryRows = useMemo(() => {
    if (!kpis) return []

    return [
      {
        id: 'totalCost',
        metric: 'Total Cost',
        currValue: kpis.totalCost,
      },
      {
        id: 'totalDistance',
        metric: 'Total Travel Distance',
        currValue: kpis.totalDistance,
      },
      {
        id: 'assignedOrderCount',
        metric: 'Allocated Orders',
        currValue: kpis.assignedOrderCount,
      },
      {
        id: 'unassignedOrderCount',
        metric: 'Unallocated Orders',
        currValue: kpis.unassignedOrderCount,
      },
      {
        id: 'underUtilizedLoads',
        metric: 'Under-utilised Loads',
        currValue: kpis.underUtilizedLoads,
      },
      {
        id: 'maxRouteDuration',
        metric: 'Maximum Route Duration',
        currValue: kpis.maxRouteDuration,
      },
      {
        id: 'maxRouteDistance',
        metric: 'Maximum Route Distance',
        currValue: kpis.maxRouteDistance,
      },
      {
        id: 'violationCount',
        metric: 'Violation Count',
        currValue: kpis.violationCount,
      }]
  }, [kpis])

  const summaryColumns: GridColDef[] = [
    {
      field: 'metric',
      headerName: 'Metric',
      width: 200,
    },
    {
      field: 'currValue',
      headerName: 'Value',
      width: 100,
      description: 'Current Value',
    },
  ]

  return {
    loading,
    summaryRows,
    summaryColumns,
  }
}
